import { action, computed, makeObservable, observable } from 'mobx'

import ProjectDateStore, {
  MILLISECONDS_IN_DAY,
} from '~/client/src/shared/stores/ui/ProjectDate.store'

import { calculateDateRange } from '../../Constants/DateRanges'
import { DateRange, DateRangeType } from '../../types/Widgets'

export default class DateRangeSelectorStore {
  @observable public selectedType: DateRangeType = DateRangeType.Week
  @observable public customStart: Date | null = null
  @observable public customEnd: Date | null = null
  @observable public projectStartDate: Date | null = null
  @observable public periodOffset: number = 0

  private projectDateStore: ProjectDateStore

  public constructor(projectDateStore: ProjectDateStore) {
    makeObservable(this)
    this.projectDateStore = projectDateStore
  }

  @computed
  public get dateRange(): DateRange {
    const baseRange = calculateDateRange(
      this.selectedType,
      this.projectDateStore,
      this.projectStartDate || undefined,
    )

    // If there's an offset, adjust the date range
    // Note: Cannot navigate for "entire project" (null start date)
    if (this.periodOffset !== 0 && baseRange.start !== null) {
      const daysDiff = Math.ceil(
        (baseRange.end.getTime() - baseRange.start.getTime()) /
          MILLISECONDS_IN_DAY,
      )

      const offsetMs = this.periodOffset * daysDiff * MILLISECONDS_IN_DAY

      return {
        start: new Date(baseRange.start.getTime() + offsetMs),
        end: new Date(baseRange.end.getTime() + offsetMs),
        type: this.selectedType,
      }
    }

    return baseRange
  }

  @computed
  public get formattedDateDisplay(): string {
    const { start, end } = this.dateRange

    if (start === null) {
      return 'Entire Project'
    }

    const startDateStr = this.projectDateStore.getDashedFormattedDate(start)
    const endDateStr = this.projectDateStore.getDashedFormattedDate(end)

    if (startDateStr === endDateStr) {
      return this.projectDateStore.getMonthDayAndYearToDisplay(start)
    }

    return `${this.projectDateStore.getMonthDayAndYearToDisplay(
      start,
    )} - ${this.projectDateStore.getMonthDayAndYearToDisplay(end)}`
  }

  @computed
  public get canNavigateNext(): boolean {
    // Can navigate forward only if we're in the past (offset < 0)
    return this.periodOffset < 0
  }

  @action.bound
  public setDateRangeType(type: DateRangeType) {
    this.selectedType = type
    // Reset offset when changing date range type
    this.periodOffset = 0
  }

  @action.bound
  public setProjectStartDate(date: Date) {
    this.projectStartDate = date
  }

  @action.bound
  public resetToToday() {
    this.periodOffset = 0
  }

  @action.bound
  public navigatePrevious() {
    // Move back one period
    this.periodOffset -= 1
  }

  @action.bound
  public navigateNext() {
    // Move forward one period (but don't go beyond current period)
    if (this.periodOffset < 0) {
      this.periodOffset += 1
    }
  }
}
